const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost/backend-twa/public';
const API_TOKEN = process.env.REACT_APP_API_TOKEN || '';

/** Hostinger MySQL allows very few concurrent connections — never stampede the API. */
const MAX_CONCURRENT = 2;
let activeCount = 0;
const waitQueue = [];

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function acquireSlot() {
  if (activeCount < MAX_CONCURRENT) {
    activeCount += 1;
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    waitQueue.push(resolve);
  }).then(() => {
    activeCount += 1;
  });
}

function releaseSlot() {
  activeCount = Math.max(0, activeCount - 1);
  const next = waitQueue.shift();
  if (next) next();
}

async function request(endpoint, options = {}) {
  const url = `${API_BASE}${endpoint}`;
  const method = (options.method || 'GET').toUpperCase();
  const headers = { ...(options.headers || {}) };
  const maxAttempts = method === 'GET' ? 4 : 1;

  if (API_TOKEN && !headers['X-Api-Key']) {
    headers['X-Api-Key'] = API_TOKEN;
  }

  // Only set JSON content-type when sending a body (avoids extra CORS preflight on GET)
  if (options.body && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  await acquireSlot();

  let response;
  let lastNetworkError = null;

  try {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        response = await fetch(url, {
          ...options,
          method,
          headers,
        });
        lastNetworkError = null;

        // Soft-retry when shared hosting returns DB connection failures
        if (response.status >= 500 && method === 'GET' && attempt < maxAttempts) {
          let shouldRetry = false;
          try {
            const peek = await response.clone().json();
            const msg = String(peek?.message || '').toLowerCase();
            shouldRetry = msg.includes('database connection');
          } catch {
            shouldRetry = true;
          }
          if (shouldRetry) {
            await sleep(250 * attempt);
            continue;
          }
        }
        break;
      } catch (err) {
        lastNetworkError = err;
        if (attempt < maxAttempts) {
          await sleep(200 * attempt);
        }
      }
    }
  } finally {
    releaseSlot();
  }

  if (!response) {
    const detail = lastNetworkError && lastNetworkError.message
      ? ` (${lastNetworkError.message})`
      : '';
    throw new Error(
      `Cannot reach the API at ${url}.${detail} Check CMS Site Settings → CORS includes this website, and Site API Token matches the frontend build.`
    );
  }

  let data;
  try {
    data = await response.json();
  } catch {
    throw new Error('API returned an invalid response');
  }

  if (!response.ok || data.success === false) {
    throw new Error(data.message || 'API request failed');
  }

  return data;
}

export const api = {
  getTours: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return request(`/api/tours${query ? `?${query}` : ''}`);
  },
  getTour: (id) => request(`/api/tours/${id}`),
  getDestinations: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return request(`/api/destinations${query ? `?${query}` : ''}`);
  },
  getDestination: (id) => request(`/api/destinations/${id}`),
  getActivities: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return request(`/api/activities${query ? `?${query}` : ''}`);
  },
  getActivity: (id) => request(`/api/activities/${id}`),
  getServices: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return request(`/api/services${query ? `?${query}` : ''}`);
  },
  getService: (id) => request(`/api/services/${id}`),
  getResorts: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return request(`/api/resorts${query ? `?${query}` : ''}`);
  },
  getResort: (id) => request(`/api/resorts/${id}`),
  getGuides: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return request(`/api/guides${query ? `?${query}` : ''}`);
  },
  getGuide: (id) => request(`/api/guides/${id}`),
  getBlogPosts: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return request(`/api/blog${query ? `?${query}` : ''}`);
  },
  getBlogPost: (id) => request(`/api/blog/${id}`),
  getProducts: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return request(`/api/products${query ? `?${query}` : ''}`);
  },
  getProduct: (id) => request(`/api/products/${id}`),
  getTestimonials: () => request('/api/testimonials'),
  getFaqs: () => request('/api/faqs'),
  getSocialVideos: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return request(`/api/social-videos${query ? `?${query}` : ''}`);
  },
  getBanners: (placement = 'home_hero') => request(`/api/banners?placement=${placement}`),
  getSettings: () => request('/api/settings'),
  getContent: (page = 'home') => request(`/api/content?page=${encodeURIComponent(page)}`),
  getGallery: (key) => request(`/api/galleries?key=${encodeURIComponent(key)}`),
  getCategories: (type = '') => {
    const query = type ? `?type=${encodeURIComponent(type)}` : '';
    return request(`/api/categories${query}`);
  },
  getTags: () => request('/api/tags'),
  getPage: (slug) => request(`/api/pages/${encodeURIComponent(slug)}`),
  submitContact: (payload) => request('/api/contact', { method: 'POST', body: JSON.stringify(payload) }),
  submitBooking: (payload) => request('/api/bookings', { method: 'POST', body: JSON.stringify(payload) }),
  subscribeNewsletter: (payload) => request('/api/newsletter', { method: 'POST', body: JSON.stringify(payload) }),
};

export function getListItems(response) {
  if (Array.isArray(response?.data)) {
    return response.data;
  }
  return response?.data?.items || [];
}

export default api;
